"use client";

import { useEffect, useState } from "react";
import { ArrowLeft, Package } from "lucide-react";

import {
  getOrderDetails,
  type OrderDetailsResponse,
} from "@/lib/api/business-api";
import { usePageTranslation } from "@/hooks/usePageTranslation";
import { toIntlDateLocale, toIntlLocale } from "@/lib/i18n/intl-locale";

import { translateOrderStatus } from "./order-status";

interface OrderDetailsProps {
  orderId: string;
  userData: any;
  businessId: string;
  onBack: () => void;
}

const headingFontStyle = { fontFamily: "var(--brand-heading-font, inherit)" };

export function OrderDetails({ orderId, userData: _userData, businessId, onBack }: OrderDetailsProps) {
  const { t, currentLanguage } = usePageTranslation();
  const [orderData, setOrderData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchOrderDetails();
  }, [orderId, businessId]);

  const fetchOrderDetails = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Get access token from storage using auth-utils
      const accessToken = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
      if (!accessToken) {
        throw new Error(t("account.noAccessToken"));
      }

      const response: OrderDetailsResponse = await getOrderDetails(
        accessToken,
        orderId,
        businessId
      );

      if (response.status && response.data) {
        setOrderData(response.data);
      } else {
        throw new Error(t("account.fetchOrderDetailsFailed"));
      }
    } catch (err: any) {
      console.error("Error fetching order details:", err);
      setError(err.message || t("account.loadOrderDetailsFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat(toIntlLocale(currentLanguage), {
      style: "currency",
      currency: orderData?.currency || "GBP",
    }).format(price);
  };

  const formatOrderDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(toIntlDateLocale(currentLanguage), {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      completed: "text-brand-success",
      processing: "text-blue-600",
      pending: "text-brand-warning",
      cancelled: "text-brand-danger",
      confirmed: "text-purple-600",
    };
    return statusColors[status.toLowerCase()] || "text-brand-muted";
  };

  const getPaymentStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      paid: "text-brand-success",
      pending: "text-brand-warning",
      failed: "text-brand-danger",
    };
    return statusColors[status.toLowerCase()] || "text-brand-muted";
  };

  const backButton = (
    <button
      onClick={onBack}
      className="mb-6 flex items-center space-x-2 text-brand-primary transition-colors hover:text-brand-primary-hover"
    >
      <ArrowLeft className="h-5 w-5 rtl:-scale-x-100" />
      <span className="font-medium">{t("account.backToOrders")}</span>
    </button>
  );

  if (isLoading) {
    return (
      <div className="order-details">
        {backButton}
        <div className="flex min-h-[400px] items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-brand-primary" />
        </div>
      </div>
    );
  }

  if (error || !orderData) {
    return (
      <div className="order-details">
        {backButton}
        <div className="rounded-brand border border-brand-danger/30 bg-brand-danger/10 p-8 text-center">
          <p className="mb-4 text-brand-danger">
            {error || t("account.orderNotFound")}
          </p>
          <button
            onClick={fetchOrderDetails}
            className="rounded-brand bg-brand-danger px-6 py-2 font-semibold text-brand-danger-foreground transition-colors hover:bg-brand-danger/90"
          >
            {t("common.tryAgain")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="order-details">
      {/* Back Button */}
      {backButton}

      <div className="mx-auto max-w-3xl">
        {/* Order Details Card */}
        <div className="mb-8 rounded-brand-lg border border-brand-border bg-brand-surface p-8 shadow-lg">
          <div className="mb-6 border-b border-brand-border pb-6">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2
                  className="text-xl font-semibold text-brand-fg"
                  style={headingFontStyle}
                >
                  {t("orderSuccess.orderDetails")}
                </h2>
                <p className="mt-1 text-2xl font-bold text-brand-primary" dir="ltr">
                  {orderData.order_number || orderData.order_id}
                </p>
                <p className="mt-1 text-sm text-brand-muted">
                  {t("orderSuccess.orderIdLabel")}{" "}
                  <span dir="ltr">{orderData.order_id}</span>
                </p>
              </div>
              <div className="rounded-full bg-brand-primary/10 p-3">
                <Package className="h-8 w-8 text-brand-primary" />
              </div>
            </div>

            {/* Order Status Information */}
            <div className="mb-4 grid grid-cols-2 gap-4 rounded-brand bg-brand-fg/5 p-4">
              <div>
                <p className="text-xs text-brand-muted">
                  {t("orderSuccess.orderStatus")}
                </p>
                <p
                  className={`mt-1 font-semibold ${getStatusColor(orderData.order_status)}`}
                >
                  {translateOrderStatus(t, orderData.order_status)}
                </p>
              </div>
              <div>
                <p className="text-xs text-brand-muted">
                  {t("orderSuccess.paymentStatus")}
                </p>
                <p
                  className={`mt-1 font-semibold ${getPaymentStatusColor(orderData.payment_status)}`}
                >
                  {translateOrderStatus(t, orderData.payment_status)}
                </p>
              </div>
              <div>
                <p className="text-xs text-brand-muted">
                  {t("orderSuccess.paymentMethod")}
                </p>
                <p className="mt-1 font-semibold text-brand-fg capitalize">
                  {orderData.payment_method?.replace("-", " ")}
                </p>
              </div>
              <div>
                <p className="text-xs text-brand-muted">
                  {t("orderSuccess.totalItems")}
                </p>
                <p className="mt-1 font-semibold text-brand-fg">
                  {orderData.items_count || orderData.items?.length || 0}
                </p>
              </div>
            </div>

            {/* Estimated Delivery */}
            {orderData.estimated_delivery && (
              <div className="rounded-brand border border-brand-primary/30 bg-brand-primary/5 p-4">
                <p className="text-xs text-brand-muted">
                  {t("orderSuccess.estimatedDelivery")}
                </p>
                <p className="mt-1 font-semibold text-brand-fg">
                  {orderData.estimated_delivery}
                </p>
              </div>
            )}

            {orderData.created_at && (
              <p className="mt-4 text-xs text-brand-muted">
                {t("orderSuccess.orderPlacedOn", {
                  date: formatOrderDate(orderData.created_at),
                })}
              </p>
            )}
          </div>

          {/* Customer & Delivery Information */}
          <div className="mb-6 border-b border-brand-border pb-6">
            <h3
              className="mb-4 text-lg font-semibold text-brand-fg"
              style={headingFontStyle}
            >
              {t("orderSuccess.deliveryInfo")}
            </h3>
            <div className="rounded-brand bg-brand-fg/5 p-4">
              <p className="font-medium text-brand-fg">
                {orderData.customer?.name}
              </p>
              <p className="mt-2 text-sm text-brand-muted">
                {orderData.delivery_address?.address_line_1}
              </p>
              {orderData.delivery_address?.address_line_2 && (
                <p className="text-sm text-brand-muted">
                  {orderData.delivery_address.address_line_2}
                </p>
              )}
              <p className="text-sm text-brand-muted">
                {orderData.delivery_address?.city}
                {orderData.delivery_address?.county &&
                  `, ${orderData.delivery_address.county}`}
              </p>
              <p className="text-sm text-brand-muted">
                {orderData.delivery_address?.postcode}
              </p>
              <p className="text-sm text-brand-muted">
                {orderData.delivery_address?.country}
              </p>
              <p className="mt-2 text-sm text-brand-muted">
                {t("orderSuccess.phoneLabel")}{" "}
                <span dir="ltr">{orderData.customer?.phone}</span>
              </p>
              <p className="text-sm text-brand-muted">
                {t("orderSuccess.emailLabel")}{" "}
                <span dir="ltr">{orderData.customer?.email}</span>
              </p>
            </div>
          </div>

          {/* Order Items */}
          <div className="mb-6 border-b border-brand-border pb-6">
            <h3
              className="mb-4 text-lg font-semibold text-brand-fg"
              style={headingFontStyle}
            >
              {t("orderSuccess.orderItems", {
                count: orderData.items_count || orderData.items?.length || 0,
              })}
            </h3>
            <div className="space-y-3">
              {orderData.items?.map((item: any, index: number) => (
                <div
                  key={`${item.product_id || item.id}-${index}`}
                  className="flex items-start justify-between gap-4 rounded-brand bg-brand-fg/5 p-3"
                >
                  <div className="flex-grow">
                    <h4 className="font-medium text-brand-fg">{item.name}</h4>
                    {item.variant && (
                      <p className="mt-1 text-sm text-brand-muted">
                        {t("orderSuccess.variantLabel", {
                          variant: item.variant,
                        })}
                      </p>
                    )}
                    <div className="mt-1 flex items-center gap-4 text-sm text-brand-muted">
                      <span>
                        {t("orderSuccess.qty", { quantity: item.quantity })}
                      </span>
                      <span>×</span>
                      <span>{formatPrice(item.price)}</span>
                    </div>
                    {item.discount > 0 && (
                      <p className="mt-1 text-xs text-brand-success">
                        {t("cart.discount")}: -{formatPrice(item.discount)}
                      </p>
                    )}
                  </div>
                  <div className="text-end">
                    <p className="font-semibold text-brand-fg">
                      {formatPrice(item.subtotal || item.price * item.quantity)}
                    </p>
                    {item.vat > 0 && (
                      <p className="mt-1 text-xs text-brand-muted">
                        {t("orderSuccess.vatIncluded", {
                          amount: formatPrice(item.vat),
                        })}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary */}
          <div>
            <h3
              className="mb-4 text-lg font-semibold text-brand-fg"
              style={headingFontStyle}
            >
              {t("cart.orderSummary")}
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between text-brand-fg">
                <span>{t("cart.subtotal")}</span>
                <span>
                  {formatPrice(
                    orderData.order_summary?.subtotal || orderData.subtotal || 0
                  )}
                </span>
              </div>
              {(orderData.order_summary?.discount || orderData.total_discount || 0) >
                0 && (
                <div className="flex justify-between text-brand-success">
                  <span>{t("cart.discount")}</span>
                  <span>
                    -
                    {formatPrice(
                      orderData.order_summary?.discount ||
                        orderData.total_discount ||
                        0
                    )}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-brand-fg">
                <span>{t("cart.tax")}</span>
                <span>
                  {formatPrice(
                    orderData.order_summary?.vat ||
                      orderData.total_vat ||
                      0
                  )}
                </span>
              </div>
              <div className="flex justify-between text-brand-fg">
                <span>{t("common.delivery")}</span>
                <span>
                  {(orderData.order_summary?.delivery_charge ||
                    orderData.delivery_charge ||
                    0) === 0
                    ? t("common.free")
                    : formatPrice(
                        orderData.order_summary?.delivery_charge ||
                          orderData.delivery_charge ||
                          0
                      )}
                </span>
              </div>
              {(orderData.order_summary?.payment_fee ||
                orderData.payment_fee_amount ||
                0) > 0 && (
                <div className="flex justify-between text-brand-fg">
                  <span>{t("common.paymentFee")}</span>
                  <span>
                    {formatPrice(
                      orderData.order_summary?.payment_fee ||
                        orderData.payment_fee_amount ||
                        0
                    )}
                  </span>
                </div>
              )}
              <div className="flex justify-between border-t-2 border-brand-border pt-3 text-lg font-bold text-brand-fg">
                <span>{t("cart.total")}</span>
                <span className="text-brand-primary">
                  {formatPrice(
                    orderData.order_summary?.total || orderData.total || 0
                  )}{" "}
                  {orderData.currency || ""}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-center">
          <button
            onClick={onBack}
            className="flex items-center justify-center gap-2 rounded-brand bg-brand-primary px-8 py-3 font-semibold text-brand-primary-foreground transition-all hover:bg-brand-primary-hover"
          >
            <ArrowLeft className="h-5 w-5 rtl:-scale-x-100" />
            {t("account.backToOrders")}
          </button>
        </div>
      </div>
    </div>
  );
}
